/**
 * FeaturedLessonPanel.jsx — Admin surface for the Featured Lesson MVP.
 *
 * Admin-only. Renders inside the /admin page as one of the tabs. Lets
 * the founder browse pinnable lessons (their own library + community-
 * shared lessons), pin one as "This Week's Featured Lesson", or unpin
 * the current selection.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Pin, PinOff, RefreshCw } from "lucide-react";
import {
  fetchFeaturedLessonAdmin,
  fetchFeaturedLessonCandidates,
  pinFeaturedLesson,
  unpinFeaturedLesson,
} from "@/lib/featuredLessonApi";

const FeaturedLessonPanel = () => {
  const [pin, setPin] = useState(null);
  const [candidates, setCandidates] = useState({ library: [], community: [] });
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [current, cand] = await Promise.all([
        fetchFeaturedLessonAdmin(),
        fetchFeaturedLessonCandidates(),
      ]);
      setPin(current);
      setCandidates(cand);
      if (current?.pinned && current.activity_id) {
        setSelectedId(current.activity_id);
      }
    } catch (err) {
      toast.error("Could not load featured lesson data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPin = async () => {
    if (!selectedId || busy) return;
    setBusy(true);
    try {
      const res = await pinFeaturedLesson(selectedId);
      setPin({ pinned: true, ...res });
      toast.success(`Pinned: ${res.activity_name || "lesson"}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not pin lesson.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const onUnpin = async () => {
    if (busy || !pin?.pinned) return;
    if (!window.confirm("Unpin the featured lesson? Free users will no longer see the card. Existing email-delivery links continue to work for 7 days.")) return;
    setBusy(true);
    try {
      await unpinFeaturedLesson();
      setPin({ pinned: false });
      setSelectedId("");
      toast.success("Featured lesson unpinned.");
    } catch (err) {
      toast.error("Could not unpin lesson.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="featured-lesson-panel" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            <Sparkles className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
            Featured Lesson MVP
          </p>
          <h2 className="mt-1 font-black text-xl text-[#1c2d3d]">
            Pick this week's free-tier lesson
          </h2>
          <p className="mt-1 text-sm text-[#4a6984] max-w-2xl leading-relaxed">
            Free users see the pinned lesson on their dashboard and can email themselves a delivery link (PDF + slide deck, valid 7 days). Existing delivery tokens stay valid even if you swap the pin.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading || busy}
          data-testid="featured-lesson-refresh"
          className="inline-flex items-center gap-1.5 rounded-md border border-[#4a6984]/20 bg-white px-3 py-1.5 text-xs font-extrabold uppercase tracking-[0.14em] text-[#4a6984] hover:bg-[#f4f5f7] disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Current pin */}
      <div
        data-testid="featured-lesson-current"
        className="rounded-2xl border border-[#4a6984]/15 bg-white p-5 sm:p-6"
      >
        {loading ? (
          <p className="text-sm text-[#4a6984]">Loading…</p>
        ) : pin?.pinned ? (
          <>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
              <Pin className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
              Currently pinned
            </p>
            <h3
              data-testid="featured-lesson-current-name"
              className="mt-2 font-black text-lg text-[#1c2d3d]"
            >
              {pin.activity_name}
            </h3>
            <p className="mt-1 text-xs font-extrabold uppercase tracking-[0.14em] text-[#4a6984]">
              {[pin.grade, pin.subject].filter(Boolean).join(" · ")}
            </p>
            {pin.tagline ? (
              <p className="mt-2 text-sm italic text-[#1c2d3d]">{pin.tagline}</p>
            ) : null}
            <p className="mt-3 text-xs text-[#4a6984]/70">
              Pinned {pin.pinned_at ? new Date(pin.pinned_at).toLocaleString() : ""}
              {pin.pinned_by ? ` by ${pin.pinned_by}` : ""}
            </p>
            <button
              type="button"
              onClick={onUnpin}
              disabled={busy}
              data-testid="featured-lesson-unpin"
              className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-[#4a6984]/30 bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-[#4a6984] hover:bg-[#f4f5f7] disabled:opacity-50"
            >
              <PinOff className="h-3.5 w-3.5" />
              Unpin
            </button>
          </>
        ) : (
          <p
            data-testid="featured-lesson-none-pinned"
            className="text-sm text-[#4a6984]"
          >
            No lesson pinned. Free users see the standard dashboard until you choose one below.
          </p>
        )}
      </div>

      {/* Candidate picker */}
      <div className="rounded-2xl border border-[#4a6984]/15 bg-white p-5 sm:p-6">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
          Choose a lesson
        </p>
        <label className="mt-3 block">
          <span className="sr-only">Lesson</span>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            data-testid="featured-lesson-select"
            disabled={loading || busy}
            className="mt-1 w-full rounded-md border border-[#4a6984]/25 bg-white px-3 py-2.5 text-sm text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1] focus:border-[#96ded1]"
          >
            <option value="">— select a lesson —</option>
            {candidates.library.length > 0 ? (
              <optgroup label="Your library">
                {candidates.library.map((c) => (
                  <option key={c.activity_id} value={c.activity_id}>
                    {c.activity_name} · {[c.grade, c.subject].filter(Boolean).join(" · ")}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {candidates.community.length > 0 ? (
              <optgroup label="Community shared">
                {candidates.community.map((c) => (
                  <option key={c.activity_id} value={c.activity_id}>
                    {c.activity_name} · {[c.grade, c.subject].filter(Boolean).join(" · ")}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </label>
        <button
          type="button"
          onClick={onPin}
          disabled={!selectedId || busy || loading}
          data-testid="featured-lesson-pin"
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Pin className="h-3.5 w-3.5" />
          {pin?.pinned && pin.activity_id === selectedId ? "Re-pin" : "Pin as featured"}
        </button>
        <p className="mt-3 text-xs text-[#4a6984]/70 leading-relaxed">
          Includes your own library (last 40) and any lessons shared to the community. Pinning is idempotent — you can swap or re-pin anytime.
        </p>
      </div>
    </div>
  );
};

export default FeaturedLessonPanel;
